import React, { FC, ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import '../../src/tooltip.css';
import TableTab from '../components/tabs/table-tab';
import { toast } from "react-toastify";
import { CUSTOM_VALUES, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from '../constants/command-status';
import { DEF_PG_SIZE_TVIEWER } from '../constants/maximum-page-size';
import { useLocale } from '../contexts/localeContext';
import { TranslationKeys } from '../contexts/TranslationKeys';
import accessBellIcon from '../images/AccessBell.svg';
import alarmBellIcon from '../images/AlarmBell.svg';
import ControllerIcon from '../images/Controller.svg';
import deniedBellIcon from '../images/DeniedBell.svg';
import ReaderIcon from '../images/Reader.svg';
import statusBellIcon from '../images/StatusBell.svg';
import UserDefaultProfile from '../images/UserProfileDefault.svg';
import { Category } from '../models/Category';
import columns from '../models/ColumnDefinitions/TransactionViewerColumn';
import { ListQuery } from '../models/ListQuery';
import { Lookup } from '../models/Lookup';
import CategorySearch from '../models/search/CategorySearch';
import DeviceImproxSearch from '../models/search/DeviceImproxSearch';
import DoorSearch from '../models/search/DoorSearch';
import EventTypeSearch from '../models/search/EventTypeSearch';
import SiteSearch from '../models/search/SiteSearch';
import searchColumns from '../models/search/TransactionViewerSearch';
import ZoneSearch from '../models/search/ZoneSearch';
import { TransactionViewer } from '../models/TransactionViewer';
import '../pages/access-control/monitoring/transaction-event.css';
import { getDeviceImproxNames } from '../services/deviceImproxService';
import { getDoorNamesWithPagination } from '../services/doorService';
import { getPersonByID } from '../services/enrolmentService';
import { getEventTypeNamesWithPagination } from '../services/eventTypeService';
import { getAllEnrolmentProfileNames } from '../services/profileService';
import { getSiteNamesWithPagination } from '../services/siteService';
import { getZoneNamesWithPagination } from '../services/zoneService';
import { IAppState } from '../store/types';

import Tooltip from '../components/Tooltip/Tooltip';
import { MONITORING_TRANSACTIONS } from '../constants/menu-types';
import { TableContextProvider } from '../contexts/table/TableContextProvider';
import { useIcaWebsocketContext } from '../contexts/websocket/IcaWebsocketContext';
import { ListData } from '../models/ListData';
import { TableLegendItem } from '../models/table/TableLegendItem';
import { showError } from '../services/utilities/ServiceError';
import { showTagCode } from '../utilities/Permissions';
import TransactionQuickEnrollModal from '../components/dashboard/quick-enrol/transaction-quick-enrol';
import { isQuickEnrollEvent } from '../constants/event-types';
import OssDeviceSearch from '@/models/search/OssDeviceSearch';
import { getOssDeviceNamesWithPagination } from '@/services/ossDeviceService';
import { LICENSE_APERIO_OSS } from '@/constants/license-constants';
import { useGlobalState } from '@/Providers';
import { hideColumns } from '@/models/utilities/columnHelper';

interface TransactionsViewerTabProps {
    categories: Category[];
    setCategories: (categoriws: Category[]) => void;
}

interface Thumbnail {
    imageURL: any;
    event: string;
    tooltipText: string;
    dateTime: string;
}

export const eventStatusFilterKey = 'status';
export const eventCategories = {
    access: { value: 'allowed', id: "1" },
    denied: { value: 'denied', id: "2" },
    alarm: { value: 'alarm', id: "3" },
    status: { value: 'status', id: "4" },
};

const TransactionsViewerTab: FC<TransactionsViewerTabProps> = ({ categories, setCategories }) => {
    const { translate } = useLocale();
    // TODO find a better way to track row changes, we probably might have to add a fix to the filters
    let hasMounted = false;
    const title = translate.byKey(TranslationKeys.transactions);
    const [rows, setRows] = useState<TransactionViewer[]>([])
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [hasReceivedFirstMessage, setHasReceivedFirstMessage] = useState(false);
    let oldRows: TransactionViewer[] = [];
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [eventHistoryThumbnails, setEventHistoryThumbnails] = useState<ReactElement[]>();
    const lastFiveImages: Thumbnail[] = []
    const [openQuickEnrol, setOpenQuickEnrol] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<TransactionViewer | null>(null);
    const [eventLegend] = useState<TableLegendItem[]>(
        [
            {
                value: eventCategories.access.id,
                imageIcon: accessBellIcon,
                label: translate.byKey(TranslationKeys.access),
                tooltip: '',
                isSelected: true
            },
            {
                value: eventCategories.denied.id,
                imageIcon: deniedBellIcon,
                label: translate.byKey(TranslationKeys.denied),
                tooltip: '',
                isSelected: true
            },
            {
                value: eventCategories.alarm.id,
                imageIcon: alarmBellIcon,
                label: translate.byKey(TranslationKeys.alarm),
                tooltip: '',
                isSelected: true
            },
            {
                value: eventCategories.status.id,
                imageIcon: statusBellIcon,
                label: translate.byKey(TranslationKeys.status),
                tooltip: '',
                isSelected: true
            }
        ]
    );
    const icaWebSocket = useIcaWebsocketContext();
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = MONITORING_TRANSACTIONS;
    const { buildInfo } = useGlobalState() ?? {};
    const isLicensedForAperioOss = buildInfo?.licenceIds?.includes(LICENSE_APERIO_OSS);

    const lookup: Lookup[] = [
        {
            name: 'site',
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'door',
            values: [],
            dataMethod: getDoorNamesWithPagination,
            searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'zone',
            values: [],
            dataMethod: getZoneNamesWithPagination,
            searchAccessor: ZoneSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'category',
            values: [],
            dataMethod: getAllEnrolmentProfileNames,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'device',
            values: [],
            dataMethod: getDeviceImproxNames,
            searchAccessor: DeviceImproxSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'event',
            values: [],
            dataMethod: getEventTypeNamesWithPagination,
            searchAccessor: EventTypeSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'ossDevice',
            values: [],
            dataMethod: getOssDeviceNamesWithPagination,
            searchAccessor: OssDeviceSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            // Stop any lingering topics from previous views (device states, tasks, discovery, etc.)
            // but keep MONITORING_ENGINE running since the menu widget always needs it.
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TASKS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DISCOVERY, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENROL, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_QUEUE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);

            // Now start only the transactions topic for this view
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessageReceived);

            // Safety fallback: if the websocket connects but no initial message arrives within
            // 15 seconds (e.g. no recent transactions), clear the spinner so the user sees the
            // "Waiting for first update" message rather than being stuck on a loading indicator.
            // Note: We only hide the spinner here, NOT mark that we've received a message.
            // The message will persist until an actual websocket message arrives.
            const fallbackTimer = setTimeout(() => {
                setIsInitialLoad(false);
            }, 15000);
            return () => {
                clearTimeout(fallbackTimer);
                icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            }
        }

        return () => {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState]);

    const onMessageReceived = async (data) => {
        // Clear the initial-load spinner on the first websocket message, regardless
        // of whether it carries transactions. This matches the UX contract: show the
        // spinner while waiting for the server's initial response, then hide it.
        setIsInitialLoad(false);
        setHasReceivedFirstMessage(true);
        try {
            const mapEventCategoryToStatus = eventCategory => {
                return Object.values(eventCategories).find(item => eventCategory === item.value)?.id ?? "0";
            };

            const response = JSON.parse(data.data);
            if (response.command === MONITORING_COMMAND.MONITORING_TRANSACTIONS) {
                response.events.forEach((event) => {
                    const dateTime = event.attributes.timestampLocal.replace("T", " ").replace(".000Z", "").replace(".000", "");
                    const eventId = event.attributes.subject.event.key.eventTypeId;
                   
                    const transaction: TransactionViewer = {
                        id: event.attributes.id,
                        name: event.attributes.subject.event.attributes.nameOriginal,
                        tagCode: event.attributes.subject.credential ? showTagCode(user.profileId, event.attributes.subject.credential.attributes.tagcode) : '-',
                        dateTime: dateTime,
                        event: event.attributes.subject.event.attributes.nameOriginal,
                        eventId: eventId,
                        eventCategory: event.attributes.subject.event.attributes.eventCategory.toLowerCase(),
                        device: event.attributes.source.device ? event.attributes.source.device.attributes.name : "-",
                        site: event.attributes.subject.site.attributes.siteName,
                        credentialHolder: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.attributes.mstDisplayname : '-',
                        deviceId: event.attributes.source.device ? event.attributes.source.device.key.deviceId : '-',
                        siteId: event.attributes.subject.site.attributes.siteId,
                        door: event.attributes.subject.door ? event.attributes.subject.door.attributes.doorName : '-',
                        doorId: event.attributes.subject.door ? event.attributes.subject.door.attributes.doorId : '-',
                        zone: event.attributes.subject.zone ? event.attributes.subject.zone.attributes.zoneName : '-',
                        zoneId: event.attributes.subject.zone ? event.attributes.subject.zone.attributes.zoneId : '-',
                        category: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.attributes.enrolmentCategory.attributes.name : '-',
                        categoryId: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.attributes.enrolmentCategory.key.profileId : '-',
                        status: mapEventCategoryToStatus(event.attributes.subject.event.attributes.eventCategory.toLowerCase()).toString(),
                        credentialHolderId: event.attributes.subject.credentialHolder ? event.attributes.subject.credentialHolder.key.masterId : '-',
                        reader: event.attributes.subject.reader ? event.attributes.subject.reader.attributes.name : '-',
                        controller: event.attributes.subject.controller ? event.attributes.subject.controller.attributes.name : '-',
                        ossDevice: event.attributes.subject.ossDevice ? event.attributes.subject.ossDevice.attributes.name : '-',
                        ossDeviceId: event.attributes.subject.ossDevice ? event.attributes.subject.ossDevice.key.deviceId : '-'
                    }

                    const eventAlreadyExists = oldRows.some(row => row.id === transaction.id);

                    if (eventAlreadyExists) {
                        return;
                    }
                    oldRows.unshift(transaction);
                    // trim of the last element if transaction rows are more than 100
                    if (oldRows.length > 100) {
                        oldRows.pop();
                    }
                })

                setCategories([{ ...categories[0], count: oldRows.length }]);
                setRows([...oldRows]);
            }
        } catch (error) {
            showError(error, toast.warn)
        }
    }

    useEffect(() => {
        dataRefresh((listParameters?: ListQuery) => getTransactions(listParameters));
    }, [rows])

    const loadTransactionImage = (transaction: TransactionViewer) => {
        // Allowed/Denied access for a known person
        if (transaction.credentialHolder !== '-') {
            setCredentialImage(transaction.credentialHolderId, transaction.eventCategory, transaction.dateTime);
        }
        // Denied unknown access
        else if (transaction.eventCategory == 'denied') {
            setLastFiveImages(transaction.eventCategory, UserDefaultProfile, transaction.event, transaction.dateTime);
        }
        // Event originated from a reader
        else if (transaction.reader !== '-') {
            setLastFiveImages(transaction.eventCategory, ReaderIcon, transaction.reader, transaction.dateTime);
        }
        // Event originated from a controller
        else {
            setLastFiveImages(transaction.eventCategory, ControllerIcon, transaction.controller, transaction.dateTime)
        }
    }

    const handleRowClick = (transaction: TransactionViewer) => {
        if (isQuickEnrollEvent(transaction.eventId, transaction.credentialHolder)) {
            setSelectedTransaction(transaction);
            setOpenQuickEnrol(true);
        }
    };

    const loadImages = (oldRows) => {
        oldRows.forEach((event) => {
            loadTransactionImage(event);
        })
    }

    const getTransactions = (listParameters?: ListQuery): Promise<ListData> => {
        let modifiedRows = rows.filter(item => item[listParameters?.searchTerm?.accessor ?? 'event'].toUpperCase().includes((listParameters?.searchTerm?.value ?? '').toUpperCase()));
        if (hasMounted === false && modifiedRows.length === 0) { // this is a hack to get around the fact that the filters are applied before we even need them
            modifiedRows = oldRows;
            hasMounted = true;
        }

        const allFilters = [...listParameters?.filters ?? []];
        const selectedFilter: { key: string, selectedOptions: string[] }[] = allFilters.map(filter => {
            return { key: filter.key, selectedOptions: filter.selected.map(selectedFilter => { return selectedFilter.value }) }
        });

        if (!selectedFilter.some(filter => filter.key === eventStatusFilterKey)) {
            modifiedRows = [];
        }

        modifiedRows = modifiedRows.filter(row => {
            let isMatch = true;

            if (selectedFilter.length !== 0) {
                selectedFilter.forEach(filter => {
                    if (filter.selectedOptions.length > 0) {
                        const value = row[filter.key].toString();
                        isMatch = filter.selectedOptions.includes(value) && isMatch;
                    }
                });
            }

            return isMatch;
        });

        // Show latest transactions top of the list
        modifiedRows.sort((a: any, b: any) => {
            return b.dateTime > a.dateTime ? 1 : -1;
        });

        lastFiveImages.splice(0, lastFiveImages.length);
        loadImages(modifiedRows.slice(0, 5))

        return new Promise((resolve, reject) => {
            return resolve({
                rows: modifiedRows,
                pagination: { maxPageSize: DEF_PG_SIZE_TVIEWER, pageNumber: 1, totalCount: modifiedRows.length, totalPages: 1 }
            });
        })

    }

    const setLastFiveImages = (eventCategory: string, imageURL: string, tooltipText: string, dateTime?) => {
        if (lastFiveImages.length === 5) {
            lastFiveImages.splice(0, 1);
            lastFiveImages.push({ imageURL: imageURL, event: eventCategory, tooltipText: tooltipText, dateTime });
        }
        else {
            lastFiveImages.push({ imageURL: imageURL, event: eventCategory, tooltipText: tooltipText, dateTime });
        }

        lastFiveImages.sort((a, b) => {
            const dateA = new Date(a.dateTime).getTime();
            const dateB = new Date(b.dateTime).getTime();
            return dateB - dateA;
        });

        setEventHistoryThumbnails(
            lastFiveImages.map((imageContent, index) => {
                return <div className="mr-3" key={`eventHistoryImages${index}`}>
                    <Tooltip show text={imageContent.tooltipText} direction="bottom">
                        <img src={imageContent.imageURL} className='object-contain' style={{ width: '80px', height: '56px' }} alt="1" />
                    </Tooltip>
                    <hr className={`h-1 mt-1 flex-grow ${imageContent.event.toLowerCase()}`} />
                </div>
            })
        )
    }

    const setCredentialImage = async (masterId: string, eventCategory: string, transactionId?: string) => {
        try {
            const person = await getPersonByID(translate, masterId);

            person.masterImages?.length
                ? setLastFiveImages(eventCategory, `data:image/png;base64,${person.masterImages[0].base64Thumb}`, person.name, transactionId)
                : setLastFiveImages(eventCategory, UserDefaultProfile, person.name, transactionId);
        } catch (error) {
            // TODO: Commenting out as causes a spam of toasts when a site filter is applied on the viewing operator
            // showError(error, toast.warn);

            // Unable to lookup the person, possibly due to Data Filters filtering out the person. Default to showing the generic user image in this case.
            setLastFiveImages(eventCategory, UserDefaultProfile, "Unknown - "+masterId, transactionId);
        }
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail) => void) => {
        setDetails(userLoginId, currentMenuDetail);
    }

    const emptyMessage = !hasReceivedFirstMessage ? translate.byKey(TranslationKeys.waitingForFirstUpdate) : undefined;

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={getTransactions}
        externalBusy={isInitialLoad}
        tableDescriptor={isLicensedForAperioOss ? columns : hideColumns(columns, ['ossDevice'])}
        searchDescriptor={searchColumns}
        legendItems={eventLegend}
        useTable
        useFilter
        useSearch
        useLegend
        legendKey={eventStatusFilterKey}
        legendLabel={'Transaction Type'}
        manualDataRefresh={manualDataRefresh}
        lookup={lookup}
        showAddNewButton={false}
        emptyStateMessage={emptyMessage}>
        <TableTab
            title={title}
            selectRows={false}
            isPaginationSupported={false}
            eventThumbnails={eventHistoryThumbnails}
            onRowClicked={handleRowClick}
            tableHeight="490px"
            overflowScroll  
            cyName="transactionViewer" />

        <TransactionQuickEnrollModal
            transaction={selectedTransaction}
            isOpen={openQuickEnrol}
            onClose={() => setOpenQuickEnrol(false)}
        />
    </TableContextProvider>
}

export default TransactionsViewerTab;
